import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

export default function AdminSystemList() {
  const [systems, setSystems] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/systems").then((r) => setSystems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this system and its credentials? This cannot be undone.")) return;
    await api.delete(`/admin/systems/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePublic = async (s) => {
    await api.put(`/admin/systems/${s.id}`, { ...s, is_public: !s.is_public });
    load();
  };

  const toggleFeatured = async (s) => {
    await api.put(`/admin/systems/${s.id}`, { ...s, is_featured: !s.is_featured });
    load();
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= systems.length) return;
    const arr = [...systems];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSystems(arr);
    await api.post("/admin/systems/reorder", { ordered_ids: arr.map((s) => s.id) });
  };

  const filtered = systems.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-10" data-testid="admin-systems-list">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Systems</h1>
          <p className="text-slate-500 mt-1">Create, edit, and manage all showroom systems.</p>
        </div>
        <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="admin-new-system-btn">
          <Link to="/admin/systems/new"><Plus className="w-4 h-4 mr-1.5" /> New System</Link>
        </Button>
      </div>

      <div className="mt-6">
        <Input placeholder="Search systems..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm rounded-full" data-testid="admin-systems-search" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 w-16">Order</th>
              <th className="px-4 py-3">System</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3 hidden md:table-cell">Status</th>
              <th className="px-4 py-3 hidden md:table-cell">Visibility</th>
              <th className="px-4 py-3 hidden md:table-cell">Featured</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, idx) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`admin-system-row-${s.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => move(idx, -1)} className="text-slate-400 hover:text-slate-900"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => move(idx, +1)} className="text-slate-400 hover:text-slate-900"><ArrowDown className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {s.thumbnail_url && (
                      <img src={resolveAssetUrl(s.thumbnail_url)} alt="" className="w-12 h-10 object-cover rounded-md border border-slate-200" />
                    )}
                    <div>
                      <div className="font-medium text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500 font-mono">/{s.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-600">{s.category}</td>
                <td className="px-4 py-3 hidden md:table-cell"><Badge variant="secondary">{s.status}</Badge></td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <button onClick={() => togglePublic(s)} className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`toggle-public-${s.slug}`}>
                    {s.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                    <span className="text-xs">{s.is_public ? "Public" : "Hidden"}</span>
                  </button>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <button onClick={() => toggleFeatured(s)} className="inline-flex items-center gap-1" data-testid={`toggle-featured-${s.slug}`}>
                    <Star className={`w-4 h-4 ${s.is_featured ? "fill-[#D4AF37] text-[#D4AF37]" : "text-slate-300"}`} />
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button asChild variant="ghost" size="sm" data-testid={`admin-edit-${s.slug}`}>
                    <Link to={`/admin/systems/${s.id}`}><Pencil className="w-4 h-4" /></Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`admin-delete-${s.slug}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
